import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTipCelebration } from "@/components/chat/TipCelebration";
import { FoundingShareCard } from "@/components/referrals/FoundingShareCard";

const KEY = "bloq_founding_celebrated";

// App-wide watcher: the first time we observe the user has crossed into Founding Member,
// pop confetti + auto-open their shareable Founding card. Fires once ever (localStorage-guarded).
export function FoundingWatcher() {
  const { user } = useAuth();
  const { celebrate } = useTipCelebration();
  const [showCard, setShowCard] = useState(false);

  const { data } = useQuery({
    queryKey: ["referrals-me"],
    queryFn: api.referralsMe,
    enabled: !!user,
    retry: false,
    staleTime: 60000,
  });

  const achieved = !!data?.founding?.achieved;
  const code = data?.code || "";
  const qualified = data?.referrals_qualified || data?.founding?.threshold || 3;

  useEffect(() => {
    if (!achieved) return;
    let done = false;
    try {
      done = localStorage.getItem(KEY) === "1";
    } catch {
      /* ignore */
    }
    if (done) return;
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* ignore */
    }
    celebrate({ variant: "founding", title: "You're a Founding Member!", subtitle: "Lifetime 50% off BLOQ+ — share your card" });
    setShowCard(true);
  }, [achieved, celebrate]);

  if (!achieved) return null;

  return (
    <Dialog open={showCard} onOpenChange={setShowCard}>
      <DialogContent className="max-w-sm border-bloq-mint/25 bg-bloq-s1" data-testid="founding-celebrate-dialog">
        <DialogHeader>
          <DialogTitle className="text-center">You made Founding Member 🎉</DialogTitle>
        </DialogHeader>
        <p className="text-center text-sm text-bloq-muted">
          Post your card — friends who join with your link get 7 days of BLOQ+ free, and so do you.
        </p>
        <FoundingShareCard code={code} qualified={qualified} />
      </DialogContent>
    </Dialog>
  );
}
